import type { Dimension, FieldRef } from '@employ-engage/data';
import {
  breakdownDimensionFor,
  BreakdownKind,
  ChartableField,
  fieldKey,
} from '../report-builder-page/chart-spec-builder';

/**
 * Turns a dashboard-wide breakdown into a dimension for one tile's template.
 *
 * `applyOverrides` takes this as a function rather than importing the field
 * catalogue itself, so the override rules stay testable on plain objects and
 * free of the builder. The resolution itself is `breakdownDimensionFor` — the
 * same one the results page uses, deliberately not a second copy of the rules
 * about which questions can be a breakdown.
 */
export const resolveBreakdownFor =
  (charted: ChartableField | null, fields: ChartableField[]) =>
  (breakdown: string, chartedField: FieldRef | null): Dimension | null => {
    // A tile knows its charted question from its own spec; the builder knows
    // it as a ChartableField. Either identifies the question well enough to
    // stop it being its own breakdown.
    const subject =
      charted ??
      ({
        ref: (chartedField ?? {}) as Record<string, unknown>,
        label: (chartedField as { label?: string })?.label ?? '',
      } as ChartableField);

    return breakdownDimensionFor(
      breakdown as BreakdownKind,
      subject,
      fields
    ) as Dimension | null;
  };

/** The charted question, matched back to the catalogue a template loaded. */
export const fieldForSpec = (
  chartedField: FieldRef | null,
  fields: ChartableField[]
): ChartableField | null => {
  if (!chartedField) return null;
  const key = String(
    (chartedField as { questionId?: string }).questionId ??
      (chartedField as { questionKey?: string }).questionKey ??
      (chartedField as { label?: string }).label ??
      ''
  );
  return fields.find((f) => fieldKey(f) === key) ?? null;
};
